import { refreshToken as refreshTokenReq } from "../containers/LoginPage/api/services"
import type { UserInfo } from "../containers/LoginPage/types/auth.types"
import { handleGetResponseData } from "../utils/handlers/handle-get-response-data"

export const PERSIST_KEY = "persist:ez_build"
const REFRESH_LOCK = "ez_build_token_refresh"

export interface RefreshedSession {
  access: string
  refresh: string
  user?: UserInfo
}

export type SessionRefreshFailure = "no-session" | "rejected" | "unavailable"

export class SessionRefreshError extends Error {
  readonly reason: SessionRefreshFailure

  constructor(reason: SessionRefreshFailure) {
    super(reason)
    this.name = "SessionRefreshError"
    this.reason = reason
  }
}

interface RefreshSessionArgs {
  currentAccessToken: string | null
  fallbackRefreshToken?: string | null
}

interface PersistedAuthTokens {
  accessToken: string | null
  refreshToken: string | null
}

export const readPersistedAuthTokens = (): PersistedAuthTokens => {
  try {
    const root = JSON.parse(localStorage.getItem(PERSIST_KEY) ?? "")
    const auth = JSON.parse(root?.auth ?? "")
    return {
      accessToken: auth?.accessToken ?? null,
      refreshToken: auth?.refreshToken ?? null
    }
  } catch {
    return { accessToken: null, refreshToken: null }
  }
}

interface TokenClaims {
  iat: number | null
  userId: string | null
}

const tokenClaims = (token: string | null | undefined): TokenClaims => {
  if (!token) {
    return { iat: null, userId: null }
  }
  try {
    const payload = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")
    const claims = JSON.parse(atob(payload))
    return {
      iat: typeof claims?.iat === "number" ? claims.iat : null,
      userId: claims?.user_id ? String(claims.user_id) : null
    }
  } catch {
    return { iat: null, userId: null }
  }
}

// The backend refuses a session only through AuthenticationFailed, which is
// always 401/403. apisauce resolves transport failures rather than throwing, so
// everything else here — no status at all, a 404 from a half-deployed routing
// layer, a 5xx from the gateway — says nothing about whether the token is valid.
const REFUSAL_STATUSES = [401, 403]

const isRefusal = (status: number | null | undefined): boolean =>
  !!status && REFUSAL_STATUSES.includes(status)

const exchange = async (token: string): Promise<RefreshedSession | null> => {
  const response = handleGetResponseData(await refreshTokenReq({ token }))
  const data = response?.data as Partial<RefreshedSession> | undefined

  if (response?.ok && data?.access && data?.refresh) {
    return { access: data.access, refresh: data.refresh, user: data.user }
  }

  // A success that carries no tokens is a proxy or captive portal answering in
  // the backend's place, never the backend itself refusing the session
  if (!isRefusal(response?.status)) {
    throw new SessionRefreshError("unavailable")
  }

  return null
}

const isSameAccount = (persisted: TokenClaims, current: TokenClaims): boolean =>
  current.userId !== null && persisted.userId === current.userId

// Tabs share one storage entry and each writes its own pair into it, so what
// is persisted is whatever tab acted last rather than what is newest. Only a
// newer iat proves another tab obtained a pair, and only a matching user_id
// proves it did so for this account.
const newerPair = (
  persisted: PersistedAuthTokens,
  persistedClaims: TokenClaims,
  currentClaims: TokenClaims
): RefreshedSession | null => {
  const isNewerSameAccount =
    !!persisted.accessToken &&
    !!persisted.refreshToken &&
    persistedClaims.iat !== null &&
    currentClaims.iat !== null &&
    persistedClaims.iat > currentClaims.iat &&
    isSameAccount(persistedClaims, currentClaims)

  if (!isNewerSameAccount) {
    return null
  }
  return {
    access: persisted.accessToken as string,
    refresh: persisted.refreshToken as string
  }
}

export const newerPairFromAnotherTab = (
  currentAccessToken: string | null,
  persisted: PersistedAuthTokens = readPersistedAuthTokens()
): RefreshedSession | null =>
  newerPair(
    persisted,
    tokenClaims(persisted.accessToken),
    tokenClaims(currentAccessToken)
  )

const requestNewTokens = async ({
  currentAccessToken,
  fallbackRefreshToken
}: RefreshSessionArgs): Promise<RefreshedSession> => {
  const persisted = readPersistedAuthTokens()
  const persistedClaims = tokenClaims(persisted.accessToken)
  const currentClaims = tokenClaims(currentAccessToken)

  const fromAnotherTab = newerPair(persisted, persistedClaims, currentClaims)
  if (fromAnotherTab) {
    return fromAnotherTab
  }

  const sameAccount = isSameAccount(persistedClaims, currentClaims)

  const refresh = fallbackRefreshToken ?? persisted.refreshToken
  if (!refresh) {
    throw new SessionRefreshError("no-session")
  }

  const session = await exchange(refresh)
  if (session) {
    return session
  }

  // Rotation blacklists the token it was handed, so a rejection is most often
  // another tab having already rotated this one. The shared storage entry
  // holds whatever that tab wrote, and it is worth one attempt before tearing
  // the session down — the iat comparison above cannot see a rotation the
  // backend stamped with an unchanged iat.
  if (
    persisted.refreshToken &&
    persisted.refreshToken !== refresh &&
    sameAccount
  ) {
    const recovered = await exchange(persisted.refreshToken)
    if (recovered) {
      return recovered
    }
  }

  throw new SessionRefreshError("rejected")
}

const withCrossTabLock = async (
  run: () => Promise<RefreshedSession>
): Promise<RefreshedSession> => {
  const locks = typeof navigator !== "undefined" ? navigator.locks : undefined
  return locks?.request ? await locks.request(REFRESH_LOCK, run) : run()
}

let inFlight: Promise<RefreshedSession> | null = null

export const refreshSession = (
  args: RefreshSessionArgs
): Promise<RefreshedSession> => {
  if (!inFlight) {
    inFlight = withCrossTabLock(() => requestNewTokens(args)).finally(() => {
      inFlight = null
    })
  }
  return inFlight
}
